'use client';

import { motion } from 'framer-motion';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';

export default function PartnershipBenefits({
	heading,
	subheading,
	benefits,
	backgroundColor = 'bg-gray-50',
	isFirst = false,
}: components['schemas']['DynamicZonePartnershipBenefitsComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	return (
		<SectionContainer
			className={`partnership-benefits-section ${backgroundColor} py-12 sm:py-16`}>
			<noscript>
				<style>{`
					.partnership-benefits-section h2[style],
					.partnership-benefits-section h3[style],
					.partnership-benefits-section p[style],
					.partnership-benefits-section div[style*='opacity'],
					.partnership-benefits-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mb-12 text-center">
				<SectionHeading
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
					viewport={{ once: true }}
					className="mb-6 bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
					{heading}
				</SectionHeading>
				{subheading && (
					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						viewport={{ once: true }}
						className="mx-auto max-w-3xl text-lg text-gray-600">
						{subheading}
					</motion.p>
				)}
			</div>

			<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
				{benefits?.map((benefit, index) => {
					return (
						<motion.div
							key={index}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: index * 0.1 }}
							viewport={{ once: true }}
							className="group rounded-xl bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5">
							<div className="flex flex-col gap-3">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/5 transition-colors duration-300 group-hover:bg-primary/10">
										<motion.div
											whileHover={{ scale: 1.1 }}
											transition={{
												type: 'spring',
												stiffness: 400,
												damping: 10,
											}}>
											<span className="h-5 w-5 text-primary transition-transform duration-300 group-hover:scale-110">
												{benefit.icon}
											</span>
										</motion.div>
									</div>
									<h3 className="text-lg font-bold text-gray-900 transition-colors duration-300 group-hover:text-primary">
										{benefit.title}
									</h3>
								</div>
								{benefit.description && (
									<div
										className="text-gray-600"
										dangerouslySetInnerHTML={{
											__html: benefit.description,
										}}
									/>
								)}
							</div>
						</motion.div>
					);
				})}
			</div>
		</SectionContainer>
	);
}
